/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import {
	deleteAccount,
	getUserProfile,
	updateUserNameService,
	updateUserProfileService,
} from 'api/services/AppService';
import { formatTemplateData, getTemplatesList } from 'api/services/AssetsService';
import { getListVideoAnyoneModelsList } from 'api/services/PlaygroundService.ts';
import { createPrompt } from 'api/services/StudioService';
import { aspectRatioConfig } from 'core/config/AspectRatioConfig';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { AuthenticationStatus } from 'core/constants/enums/AuthenticationStatus.ts';
import { GenerationType } from 'core/constants/enums/GenerationType.ts';
import { TemplateType } from 'core/constants/enums/TemplateType.ts';
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import { HISTORY, STUDIO, TEXT_TO_IMAGE } from 'core/navigation/Routes';
import { runGenerateCanvasMediaAsync } from 'core/store/reducers/canvasReducer/CanvasSlice';
import { generateCharacterAsync } from 'core/store/reducers/characterStudioReducer/CharaterStudioSlice';
import {
	getMusicVideoAsync,
	musicVideoUpdateScenesAsync,
} from 'core/store/reducers/intermediateMediaReducer/IntermediateMediaSlice';
import { studioHistorySelector } from 'core/store/reducers/profileReducer/ProfileSlice';
import {
	multiDeleteImageAsync,
	runGenerateStudioMediaAsync,
	studioFeedBatchWiseMapSelector,
} from 'core/store/reducers/studioReducer/StudioSlice';
import { handleSomethingWentWrongErrorWithToast } from 'core/utils/utils';

export const getPoseControlTemplatesAsync = createAsyncThunk(
	'app/getPoseControlTemplates',
	async (_, { dispatch }) => {
		return getTemplatesList(TemplateType.POSE_CONTROL, { dispatch });
	},
);

export const getGarmentsTemplatesAsync = createAsyncThunk(
	'app/getGarmentsTemplates',
	async (_, { dispatch }) => {
		return getTemplatesList(TemplateType.CLOTH_SWAP_GARMENTS, { dispatch });
	},
);

export const getTargetGarmentsTemplatesAsync = createAsyncThunk(
	'app/getTargetGarmentsTemplates',
	async (_, { dispatch }) => {
		return getTemplatesList(TemplateType.CLOTH_SWAP_TARGETS, { dispatch });
	},
);

export const getClothesSwapGuideGarmentsTemplatesAsync = createAsyncThunk(
	'app/getClothesSwapGuideGarmentsTemplates',
	async (_, { dispatch }) => {
		return getTemplatesList(TemplateType.CLOTH_SWAP_GUIDE, { dispatch });
	},
);

export const getProductPhotoTemplatesAsync = createAsyncThunk(
	'app/getProductPhotoTemplates',
	async (_, { dispatch }) => {
		return getTemplatesList(TemplateType.OBJECT_PLACEMENT, { dispatch });
	},
);
export const getProductPhotoGuideTemplatesAsync = createAsyncThunk(
	'app/getProductPhotoGuideTemplates',
	async (_, { dispatch }) => {
		return getTemplatesList(TemplateType.OBJECT_PLACEMENT_GUIDE, { dispatch });
	},
);

export const getImageReferenceStyleTemplatesAsync = createAsyncThunk(
	'app/getImageReferenceStyleTemplates',
	async (_, { dispatch }) => {
		return getTemplatesList(TemplateType.FLUX_REFERENCE_IMAGE, { dispatch });
	},
);

export const getVideoAnyoneModelsAsync = createAsyncThunk(
	'app/getVideoAnyoneModels',
	async (_, { dispatch }) => {
		return getListVideoAnyoneModelsList(GenerationType.VIDEO_ANYONE, { dispatch });
	},
);

export const getUserProfileAsync = createAsyncThunk(
	'profile/getUserProfileAsync',
	async (_, { dispatch }) => getUserProfile({ dispatch }),
);
export const deleteAccountAsync = createAsyncThunk(
	'account/deleteAccountAsync',
	async (_, { dispatch }) => deleteAccount({ dispatch }),
);

export const updateUserProfileAsync = createAsyncThunk(
	'profile/updateUserProfile',
	async (data, { dispatch }) => updateUserProfileService(data, { dispatch }),
);

export const updateUserNameAsync = createAsyncThunk(
	'profile/updateUserNameAsync',
	async (data, { dispatch }) => updateUserNameService(data, { dispatch }),
);

const initialState = {
	displayName: null,

	serverWorking: true,
	publicCanvasWorking: true,

	getUserProfileAPIStage: ApiStage.LOADING,
	getDeleteAccountAPIStage: ApiStage.IDLE,
	deleteAccountModal: false,
	userProfileDetails: {
		user_id: '',
		handle: '',

		profileUrl: '',

		blocked: '',

		name: '',
		bio: '',
		email: '',
		links: {
			buyMeCoffee: '',
			patreon: '',
			discord: '',
			instagram: '',
			x: '',
			facebook: '',
			reddit: '',
			telegram: '',
			email: '',
			others1: '',
			others2: '',
		},

		currentPlan: 'free',
		validity: 'annually',
		supportEnabled: false,
		showProratedPrices: false,

		activePurchase: {
			one_time: false, // boolean
			subscription: false,
		},
	},

	getCurrentUserAPIStage: ApiStage.LOADING,
	authenticateUserAPIStage: ApiStage.LOADING,
	currentUser: null,

	openSignInModal: false,
	openSignedOutModal: false,

	openExpandImageModal: false,
	expandImageModalDetails: null,

	expandMediaModalDetails: null,

	marketingBannerWorking: false,
	maintenanceBanner: {},
	showMarketingBanner: false,
	openMarketingPopUp: false,

	videoAnyoneImageCroppingModal: { isModelOpen: false },
	musicVideoAudioCroppingModal: { isModalOpen: false },
	musicVideoTemplateModal: false,

	openFeedPromptsModal: false,
	openFeedChangeAnythingPromptModal: false,
	feedPromptsModalType: null,
	feedPromptsModalSelectedBatchId: null,
	feedPromptsModalImageIds: [],
	feedPromptsModalSelectedImageIndex: null,

	showConfirmPromptScreen: false,

	publishPromptAPIStage: ApiStage.IDLE,

	updateUserProfileAPIStage: ApiStage.IDLE,

	userAuthenticationStatus: AuthenticationStatus.UNKNOWN,

	pusherSubscriptionStatus: ApiStage.IDLE,
	// NOTE: added userIdInitialisationStatus to avoid multiple execution of useEffect
	// handling pusher subscription on repeated call of profile API to update credits
	userIdInitialisationStatus: ApiStage.IDLE,
	isAppUpdateAvailable: false,
	activeTabs: [],
	selectedCanvasTab: 'home',
	currentOAuthToken: null,
	saleData: {},
	poseControlTemplates: null,
	poseControlTemplatesApiStage: ApiStage.IDLE,
	musicVideoCreateSceneModal: { open: false, mediaId: '' },
	garmentsTemplatesApiStage: ApiStage.IDLE,
	garmentsTemplatesData: null,
	targetGarmentsTemplatesApiStage: ApiStage.IDLE,
	targetGarmentsTemplatesData: null,
	clothesSwapGuideGarmentsTemplatesApiStage: ApiStage.IDLE,
	clothesSwapGuideGarmentsTemplatesData: null,
	productPhotoTemplatesData: null,
	productPhotoTemplatesDataApiStage: ApiStage.IDLE,
	productPhotoGuideTemplatesApiStage: ApiStage.IDLE,
	productPhotoGuideTemplatesData: null,
	productPhotoGuideToggle: false,
	imageReferenceTemplatesData: null,
	imageReferenceTemplatesApiStage: ApiStage.IDLE,
	videoAnyoneDefaultModel: null,
	videoAnyoneModelData: null,
	videoAnyoneModelsApiStage: ApiStage.IDLE,
};
export const appInitialState = initialState;
// publish prompt
// Submit prompt for review
export const publishPromptAsync = createAsyncThunk(
	'studio/createPromptAsync',
	async (data, { getState }) => {
		// eslint-disable-next-line no-use-before-define
		const selectedPrompt = feedPromptModalDetailsSelector(getState());

		const { nameInput: title, keywordsInput: keywords } = data;

		const {
			generationId: imageId,

			image,

			prompt,
			negativePrompt,

			model,
			sampler,
			guidance: cfgScale,
			steps,
			aspectRatioType,
			seed,

			clipSkip = 1,

			cost = 0,
			hires = '',
			isPaid = false,
		} = selectedPrompt;

		const { label: aspectRatio } = aspectRatioConfig[aspectRatioType];

		return createPrompt(
			imageId,

			title,
			keywords,

			image,

			prompt,
			negativePrompt,
			model,
			sampler,
			cfgScale,
			steps,
			aspectRatio,
			seed,

			clipSkip,

			cost,
			hires,
			isPaid,
		);
	},
);

export const appSlice = createSlice({
	name: 'app',
	initialState,
	reducers: {
		setServerWorking: (state, action) => {
			state.serverWorking = action.payload;
		},

		setPublicCanvasWorking: (state, action) => {
			state.publicCanvasWorking = action.payload;
		},

		setCurrentUser: (state, action) => {
			state.currentUser = action.payload;
			if (action.payload) {
				state.showMarketingBanner = true;
			}
		},
		setGetCurrentUserAPIStage: (state, action) => {
			state.getCurrentUserAPIStage = action.payload;
		},

		setAuthenticateUserAPIStage: (state, action) => {
			state.authenticateUserAPIStage = action.payload;
		},
		setUserAuthenticationStatus: (state, action) => {
			state.userAuthenticationStatus = action.payload;
		},

		toggleSignedOutModal: (state, action) => {
			state.openSignedOutModal = action.payload;
		},

		toggleOpenExpandImageModal: (state, action) => {
			const { open, details = null } = action.payload;

			state.openExpandImageModal = open;
			state.expandImageModalDetails = details;
		},

		toggleExpandMediaModal: (state, action) => {
			if (Object.hasOwn(action.payload, 'openModal')) {
				state.expandMediaModalDetails = action.payload;
			}
		},

		setMarketingBannerWorkingWorking: (state, action) => {
			state.marketingBannerWorking = action.payload;
		},
		setMaintenanceBanner: (state, action) => {
			state.maintenanceBanner = action.payload;
		},
		toggleShowMarketingBanner: (state, action) => {
			state.showMarketingBanner = action.payload;
		},
		toggleOpenMarketingPopUp: (state, action) => {
			state.openMarketingPopUp = action.payload;
		},

		toggleFeedPromptsModal: (state, action) => {
			const {
				modalType = TEXT_TO_IMAGE,
				openModal,
				batchId,
				selectedImageIndex,
				imageIdList,
			} = action.payload;

			state.openFeedPromptsModal = openModal;
			state.feedPromptsModalType = modalType;

			if (openModal) {
				state.feedPromptsModalSelectedBatchId = batchId;
				state.feedPromptsModalImageIds = imageIdList;
				state.feedPromptsModalSelectedImageIndex = selectedImageIndex;

				state.showConfirmPromptScreen = false;
			}
		},
		setFeedPromptsModalSelectedImageIndex: (state, action) => {
			state.feedPromptsModalSelectedImageIndex = action.payload;
		},

		toggleFeedChangeAnythingModal: (state, action) => {
			const {
				modalType = TEXT_TO_IMAGE,
				openModal,
				batchId,
				selectedImageIndex,
				imageIdList,
			} = action.payload;

			state.openFeedChangeAnythingPromptModal = openModal;
			state.feedPromptsModalType = modalType;

			if (openModal) {
				state.feedPromptsModalSelectedBatchId = batchId;
				state.feedPromptsModalImageIds = imageIdList;
				state.feedPromptsModalSelectedImageIndex = selectedImageIndex;

				state.showConfirmPromptScreen = false;
			}
		},

		setShowConfirmPromptScreen: (state, action) => {
			state.showConfirmPromptScreen = action.payload;
		},

		setPublishPromptAPIStage: (state, action) => {
			state.publishPromptAPIStage = action.payload;
		},

		resetUpdateUserProfileAPIStage: (state) => {
			state.updateUserProfileAPIStage = ApiStage.IDLE;
		},

		blockUser: (state) => {
			state.userProfileDetails = { ...state.userProfileDetails, blocked: true };
		},

		setDisplayName: (state, action) => {
			state.displayName = action.payload;
		},
		setBalance: (state, action) => {
			state.userProfileDetails.credits.balance = action.payload;
		},

		setPusherSubscriptionStatus: (state, action) => {
			state.pusherSubscriptionStatus = action.payload;
		},
		toggleDeletedAccountModal: (state, action) => {
			state.deleteAccountModal = action.payload;
		},
		setIsAppUpdateAvailable: (state, action) => {
			state.isAppUpdateAvailable = action.payload;
		},
		setActiveTabs: (state, action) => {
			const newTab = action.payload;
			const isTabAlreadyActive = state.activeTabs.some((tab) => tab.id === newTab.id);

			if (!isTabAlreadyActive) {
				state.activeTabs = [...state.activeTabs, newTab];
			}
		},
		setSelectedCanvasTab: (state, action) => {
			state.selectedCanvasTab = action.payload;
		},
		closeCanvasTab: (state, action) => {
			const tabId = action.payload;
			state.activeTabs = state.activeTabs.filter((tab) => tab.id !== tabId);
			if (state.selectedCanvasTab === tabId) {
				state.selectedCanvasTab =
					state.activeTabs.length > 0 ? state.activeTabs[state.activeTabs.length - 1].id : 'home';
			}
		},
		closeAllCanvasTabs: (state) => {
			state.activeTabs = [];
			state.selectedCanvasTab = 'home';
		},
		setSaleData: (state, action) => {
			state.saleData = action.payload;
		},
		setCurrentOAuthToken: (state, action) => {
			state.currentOAuthToken = action.payload;
		},
		toggleVideoAnyoneImageCroppingModal: (state, action) => {
			const {
				isModelOpen = false,
				assetUrl = null,
				assetId,
				route = null,
				actionType = null,
			} = action.payload;
			state.videoAnyoneImageCroppingModal = { isModelOpen, assetUrl, assetId, route, actionType };
		},
		toggleMusicVideoAudioCroppingModal: (state, action) => {
			const { isModalOpen = false, asset = null, route = null } = action.payload;
			state.musicVideoAudioCroppingModal = { isModalOpen, asset, route };
		},
		toggleMusicVideoTemplateModal: (state, action) => {
			state.musicVideoTemplateModal = action.payload;
		},
		toggleMusicVideoCreateSceneModal: (state, action) => {
			state.musicVideoCreateSceneModal = action.payload;
		},
		resetAppState: (state) => {
			Object.keys(initialState).forEach((key) => {
				state[key] = initialState[key];
			});
		},
		setProductPhotoGuideToggle: (state) => {
			state.productPhotoGuideToggle = !state.productPhotoGuideToggle;
		},
	},
	extraReducers: (builder) => {
		// get user profile
		builder.addCase(getUserProfileAsync.pending, (state) => {
			state.getUserProfileAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getUserProfileAsync.fulfilled, (state, action) => {
			state.getUserProfileAPIStage = ApiStage.SUCCESS;
			state.userIdInitialisationStatus = ApiStage.SUCCESS;
			state.userProfileDetails = action.payload;
		});
		builder.addCase(getUserProfileAsync.rejected, (state) => {
			state.getUserProfileAPIStage = ApiStage.ERROR;
			sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});

		// delete account
		builder.addCase(deleteAccountAsync.pending, (state) => {
			state.getDeleteAccountAPIStage = ApiStage.LOADING;
		});

		builder.addCase(deleteAccountAsync.fulfilled, (state) => {
			state.getDeleteAccountAPIStage = ApiStage.SUCCESS;
		});

		builder.addCase(deleteAccountAsync.rejected, (state) => {
			state.getDeleteAccountAPIStage = ApiStage.ERROR;
			sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});

		// publish/create prompt
		builder.addCase(publishPromptAsync.pending, (state) => {
			state.publishPromptAPIStage = ApiStage.LOADING;
		});
		builder.addCase(publishPromptAsync.fulfilled, (state) => {
			state.publishPromptAPIStage = ApiStage.SUCCESS;
		});
		builder.addCase(publishPromptAsync.rejected, (state, action) => {
			state.publishPromptAPIStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// update profile
		builder.addCase(updateUserProfileAsync.pending, (state) => {
			state.updateUserProfileAPIStage = ApiStage.LOADING;
		});
		builder.addCase(updateUserProfileAsync.fulfilled, (state, action) => {
			state.updateUserProfileAPIStage = ApiStage.SUCCESS;
			state.userProfileDetails = action.payload;
			sendToast(ToastType.UPDATE_PROFILE_SUCCESS);
		});
		builder.addCase(updateUserProfileAsync.rejected, (state, action) => {
			state.updateUserProfileAPIStage = ApiStage.ERROR;
			sendToast(ToastType.CUSTOM, `${action.error.message}`);
		});

		builder.addCase(multiDeleteImageAsync.fulfilled, (state) => {
			state.openFeedPromptsModal = false;
			state.openFeedChangeAnythingPromptModal = false;
			state.expandImageModalDetails = null;
			state.expandMediaModalDetails = null;
			state.openExpandImageModal = false;
			state.selectedImages = [];
		});

		// update user name
		builder.addCase(updateUserNameAsync.rejected, () => {
			sendToast(ToastType.FAILED_TO_SAVE_NAME);
		});
		builder.addCase(runGenerateCanvasMediaAsync.fulfilled, (state, action) => {
			state.userProfileDetails.credits.balance = action.payload.credits;

			const response = action.payload;
			const { formattedDerivedMedia } = response;

			const [mediaId] = Object.keys(formattedDerivedMedia);
			if (
				formattedDerivedMedia?.[mediaId].canvasSettings.generationType ===
				GenerationType.MUSIC_VIDEO
			) {
				state.musicVideoCreateSceneModal = {
					open: true,
					mediaId,
					route: STUDIO,
				};
			}
		});
		builder.addCase(runGenerateStudioMediaAsync.fulfilled, (state, action) => {
			if (state.userProfileDetails?.credits)
				state.userProfileDetails.credits.balance = action.payload.credits;

			const response = action.payload;
			const { batchDetails } = response;
			const mediaId = Object.keys(batchDetails.derivedMedia)[0];
			if (batchDetails.generationType === 'music_video') {
				state.musicVideoCreateSceneModal = {
					open: true,
					mediaId,
					route: TEXT_TO_IMAGE,
				};
			}
		});

		builder.addCase(musicVideoUpdateScenesAsync.fulfilled, (state, action) => {
			state.userProfileDetails.credits.balance = action.payload.data.credits;
		});

		builder.addCase(getMusicVideoAsync.fulfilled, (state, action) => {
			if (state.userProfileDetails)
				state.userProfileDetails.credits.balance = action.payload.data.credits;
			const { data } = action.payload;
			const { id: mediaId } = data;
			if (
				state.musicVideoCreateSceneModal.mediaId === mediaId &&
				state.musicVideoCreateSceneModal.open
			) {
				state.musicVideoCreateSceneModal = {
					open: false,
					mediaId: null,
					route: null,
				};
			}
		});

		builder.addCase(generateCharacterAsync.fulfilled, (state, action) => {
			state.userProfileDetails.credits.balance = action.payload.credits;
		});
		// controlNet Templates

		builder.addCase(getPoseControlTemplatesAsync.pending, (state) => {
			state.poseControlTemplatesApiStage = ApiStage.LOADING;
		});
		builder.addCase(getPoseControlTemplatesAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.poseControlTemplatesApiStage = ApiStage.SUCCESS;
			state.poseControlTemplates = data.map(formatTemplateData);
		});
		builder.addCase(getPoseControlTemplatesAsync.rejected, (state, action) => {
			state.poseControlTemplatesApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// garments templates
		builder.addCase(getGarmentsTemplatesAsync.pending, (state) => {
			state.garmentsTemplatesApiStage = ApiStage.LOADING;
		});
		builder.addCase(getGarmentsTemplatesAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.garmentsTemplatesApiStage = ApiStage.SUCCESS;
			state.garmentsTemplatesData = data.map(formatTemplateData);
		});
		builder.addCase(getGarmentsTemplatesAsync.rejected, (state, action) => {
			state.garmentsTemplatesApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// target garments templates
		builder.addCase(getTargetGarmentsTemplatesAsync.pending, (state) => {
			state.targetGarmentsTemplatesApiStage = ApiStage.LOADING;
		});
		builder.addCase(getTargetGarmentsTemplatesAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.targetGarmentsTemplatesApiStage = ApiStage.SUCCESS;
			state.targetGarmentsTemplatesData = data.map(formatTemplateData);
		});
		builder.addCase(getTargetGarmentsTemplatesAsync.rejected, (state, action) => {
			state.targetGarmentsTemplatesApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// swap guide garments templates
		builder.addCase(getClothesSwapGuideGarmentsTemplatesAsync.pending, (state) => {
			state.clothesSwapGuideGarmentsTemplatesApiStage = ApiStage.LOADING;
		});
		builder.addCase(getClothesSwapGuideGarmentsTemplatesAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.clothesSwapGuideGarmentsTemplatesApiStage = ApiStage.SUCCESS;
			state.clothesSwapGuideGarmentsTemplatesData = data.map(formatTemplateData);
		});
		builder.addCase(getClothesSwapGuideGarmentsTemplatesAsync.rejected, (state, action) => {
			state.clothesSwapGuideGarmentsTemplatesApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		builder.addCase(getProductPhotoTemplatesAsync.pending, (state) => {
			state.productPhotoTemplatesDataApiStage = ApiStage.LOADING;
		});
		builder.addCase(getProductPhotoTemplatesAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.productPhotoTemplatesDataApiStage = ApiStage.SUCCESS;
			state.productPhotoTemplatesData = data.map(formatTemplateData);
		});
		builder.addCase(getProductPhotoTemplatesAsync.rejected, (state, action) => {
			state.productPhotoTemplatesDataApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		builder.addCase(getProductPhotoGuideTemplatesAsync.pending, (state) => {
			state.productPhotoGuideTemplatesApiStage = ApiStage.LOADING;
		});
		builder.addCase(getProductPhotoGuideTemplatesAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.productPhotoGuideTemplatesApiStage = ApiStage.SUCCESS;
			state.productPhotoGuideTemplatesData = data.map(formatTemplateData);
		});
		builder.addCase(getProductPhotoGuideTemplatesAsync.rejected, (state, action) => {
			state.productPhotoGuideTemplatesApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		builder.addCase(getImageReferenceStyleTemplatesAsync.pending, (state) => {
			state.imageReferenceTemplatesApiStage = ApiStage.LOADING;
		});
		builder.addCase(getImageReferenceStyleTemplatesAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.imageReferenceTemplatesApiStage = ApiStage.SUCCESS;
			state.imageReferenceTemplatesData = data.map(formatTemplateData);
		});
		builder.addCase(getImageReferenceStyleTemplatesAsync.rejected, (state, action) => {
			state.imageReferenceTemplatesApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// get video-anyone models
		builder.addCase(getVideoAnyoneModelsAsync.pending, (state) => {
			state.videoAnyoneModelsApiStage = ApiStage.LOADING;
		});
		builder.addCase(getVideoAnyoneModelsAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.videoAnyoneModelData = data.modelsData;
			state.videoAnyoneDefaultModel = data.defaultModel;
			state.videoAnyoneModelsApiStage = ApiStage.SUCCESS;
		});
		builder.addCase(getVideoAnyoneModelsAsync.rejected, (state, action) => {
			state.videoAnyoneModelsApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});
	},
});

export const {
	setServerWorking,
	setPublicCanvasWorking,

	setCurrentUser,
	setGetCurrentUserAPIStage,
	setAuthenticateUserAPIStage,
	setUserAuthenticationStatus,

	toggleSignedOutModal,

	toggleOpenExpandImageModal,
	toggleExpandMediaModal,

	setMarketingBannerWorkingWorking,
	setMaintenanceBanner,
	toggleShowMarketingBanner,
	toggleOpenMarketingPopUp,
	setModelsCategories,

	toggleFeedPromptsModal,
	toggleFeedChangeAnythingModal,
	setFeedPromptsModalSelectedImageIndex,

	setShowConfirmPromptScreen,

	setPublishPromptAPIStage,

	resetUpdateUserProfileAPIStage,

	blockUser,

	setDisplayName,

	setBalance,

	setPusherSubscriptionStatus,
	toggleDeletedAccountModal,
	setIsAppUpdateAvailable,
	setActiveTabs,
	closeCanvasTab,
	closeAllCanvasTabs,
	setSelectedCanvasTab,
	setSaleData,
	setCurrentOAuthToken,
	toggleVideoAnyoneImageCroppingModal,
	toggleMusicVideoAudioCroppingModal,
	toggleMusicVideoTemplateModal,
	toggleMusicVideoCreateSceneModal,
	resetAppState,
	setProductPhotoGuideToggle,
} = appSlice.actions;

export const serverWorkingSelector = (state) => state.app.serverWorking;
export const publicCanvasWorkingSelector = (state) => state.app.publicCanvasWorking;

export const userBlockedSelector = (state) => state.app.userProfileDetails.blocked;

export const getUserProfileAPIStageSelector = (state) => state.app.getUserProfileAPIStage;
export const getDeleteAccountAPIStageSelector = (state) => state.app.getDeleteAccountAPIStage;
export const userProfileSelector = (state) => state.app.userProfileDetails;
export const userProfileEmailSelector = (state) => state.app.userProfileDetails?.email;
export const balanceSelector = (state) => state.app.userProfileDetails.credits?.balance;

export const currentUserSelector = (state) => state.app.currentUser;
export const authenticateUserAPIStageSelector = (state) => state.app.authenticateUserAPIStage;

export const openSignedOutModalSelector = (state) => state.app.openSignedOutModal;

export const openExpandImageModalSelector = (state) => state.app.openExpandImageModal;
export const expandImageModalDetailsSelector = (state) => state.app.expandImageModalDetails;

export const expandMediaModalDetailsSelector = (state) => state.app.expandMediaModalDetails;

export const marketingBannerWorkingSelector = (state) => state.app.marketingBannerWorking;
export const maintenanceBannerSelector = (state) => state.app.maintenanceBanner;
export const showMarketingBannerSelector = (state) => state.app.showMarketingBanner;
export const openMarketingPopUpSelector = (state) => state.app.openMarketingPopUp;

export const openFeedPromptsModalSelector = (state) => state.app.openFeedPromptsModal;
export const videoAnyoneImageCroppingModalSelector = (state) =>
	state.app.videoAnyoneImageCroppingModal;

export const musicVideoAudioCroppingModalSelector = (state) =>
	state.app.musicVideoAudioCroppingModal;
export const musicVideoTemplateModalSelector = (state) => state.app.musicVideoTemplateModal;
export const openFeedChangeAnythingModalSelector = (state) =>
	state.app.openFeedChangeAnythingPromptModal;
export const feedPromptsModalTypeSelector = (state) => state.app.feedPromptsModalType;
export const feedPromptsModalSelectedBatchIdSelector = (state) =>
	state.app.feedPromptsModalSelectedBatchId;
export const feedPromptsModalImageIdsSelector = (state) => state.app.feedPromptsModalImageIds;
export const feedPromptsModalSelectedImageIndexSelector = (state) =>
	state.app.feedPromptsModalSelectedImageIndex;
export const feedPromptModalDetailsSelector = createSelector(
	[
		openFeedPromptsModalSelector,
		feedPromptsModalTypeSelector,

		studioFeedBatchWiseMapSelector,
		studioHistorySelector,

		feedPromptsModalSelectedBatchIdSelector,
		feedPromptsModalImageIdsSelector,
		feedPromptsModalSelectedImageIndexSelector,
	],
	(
		openFeedPromptsModal,
		feedPromptsModalType,
		//
		studioFeedBatchWiseMap,
		studioHistory,
		//
		feedPromptsModalSelectedBatchId,
		feedPromptsModalImageIds,
		feedPromptsModalSelectedImageIndex,
	) => {
		if (feedPromptsModalSelectedImageIndex !== null) {
			if (feedPromptsModalType === TEXT_TO_IMAGE) {
				const batch = studioFeedBatchWiseMap[feedPromptsModalSelectedBatchId];
				const mediaId = feedPromptsModalImageIds[feedPromptsModalSelectedImageIndex];
				if (batch?.derivedMedia[mediaId] != null) {
					return {
						...batch?.derivedMedia[mediaId],
						isTrueTouch: batch.isTrueTouch,
						isChangeIt: batch.isChangeIt,
						isUpscale: batch.isUpscale,
						isFaceReplace: batch.isFaceReplace,
						isClothSwap: batch.isClothSwap,
					};
				}
				return {};
			}
			if (feedPromptsModalType === HISTORY) {
				const imageId = feedPromptsModalImageIds[feedPromptsModalSelectedImageIndex];

				const selectedPrompt = studioHistory.find((item) => item.generationId === imageId);
				const formattedPrompt = {
					...selectedPrompt,
					...selectedPrompt?.settings,
				};

				return formattedPrompt ?? {};
			}
		}
		return {};
	},
);
export const feedPromptModalHasMultipleImagesSelector = createSelector(
	[
		openFeedPromptsModalSelector,
		openFeedChangeAnythingModalSelector,
		feedPromptsModalTypeSelector,

		studioFeedBatchWiseMapSelector,
		feedPromptsModalSelectedBatchIdSelector,
	],
	(
		openFeedPromptsModal,
		openFeedChangeAnythingModal,
		feedPromptsModalType,
		//
		studioFeedBatchWiseMap,
		feedPromptsModalSelectedBatchId,
	) => {
		if (openFeedPromptsModal && feedPromptsModalSelectedBatchId !== null) {
			if (feedPromptsModalType === TEXT_TO_IMAGE) {
				const batch = studioFeedBatchWiseMap[feedPromptsModalSelectedBatchId];
				return Object.keys(batch.derivedMedia).length > 1;
			}
			if (feedPromptsModalType === HISTORY) {
				return true;
			}
		}
		if (openFeedChangeAnythingModal && feedPromptsModalSelectedBatchId !== null) {
			if (feedPromptsModalType === TEXT_TO_IMAGE) {
				const batch = studioFeedBatchWiseMap[feedPromptsModalSelectedBatchId];
				return Object.keys(batch.derivedMedia).length > 1;
			}
			if (feedPromptsModalType === HISTORY) {
				return true;
			}
		}
		return false;
	},
);

export const showConfirmPromptScreenSelector = (state) => state.app.showConfirmPromptScreen;

export const publishPromptAPIStageSelector = (state) => state.app.publishPromptAPIStage;

export const nameSelector = (state) => state.app.name;
export const updateProfileApiStageSelector = (state) => state.app.updateUserProfileAPIStage;

export const userAuthenticationStatusSelector = (state) => state.app.userAuthenticationStatus;

export const displayNameSelector = (state) => state.app.displayName;

export const pusherSubscriptionStatusSelector = (state) => state.app.pusherSubscriptionStatus;
export const userIdInitialisationStatusSelector = (state) => state.app.userIdInitialisationStatus;
export const deletedAccountStatusSelector = (state) => state.app.deleteAccountModal;

export const isAppUpdateAvailableSelector = (state) => state.app.isAppUpdateAvailable;

export const activeTabsSelector = (state) => state.app.activeTabs;

export const selectedCanvasTabSelector = (state) => state.app.selectedCanvasTab;

export const saleDataSelector = (state) => state.app.saleData;

export const currentOAuthTokenSelector = (state) => state.app.currentOAuthToken;

export const poseControlTemplatesSelector = (state) => state.app.poseControlTemplates;
export const poseControlTemplatesApiStageSelector = (state) =>
	state.app.poseControlTemplatesApiStage;

export const garmentsTemplatesDataSelector = (state) => state.app.garmentsTemplatesData;
export const garmentsTemplatesApiStageSelector = (state) => state.app.garmentsTemplatesApiStage;

export const targetGarmentsTemplatesDataSelector = (state) => state.app.targetGarmentsTemplatesData;
export const targetGarmentsTemplatesApiStageSelector = (state) =>
	state.app.targetGarmentsTemplatesApiStage;

export const clothesSwapGuideGarmentsTemplatesDataSelector = (state) =>
	state.app.clothesSwapGuideGarmentsTemplatesData;
export const clothesSwapGuideGarmentsTemplatesApiStageSelector = (state) =>
	state.app.clothesSwapGuideGarmentsTemplatesApiStage;

export const productPhotoTemplatesDataSelector = (state) => state.app.productPhotoTemplatesData;
export const productPhotoTemplatesDataApiStageSelector = (state) =>
	state.app.productPhotoTemplatesDataApiStage;
export const productPhotoGuideTemplatesDataSelector = (state) =>
	state.app.productPhotoGuideTemplatesData;
export const productPhotoGuideTemplatesApiStageSelector = (state) =>
	state.app.productPhotoGuideTemplatesApiStage;

export const imageReferenceTemplatesDataSelector = (state) => state.app.imageReferenceTemplatesData;
export const imageReferenceTemplatesApiStageSelector = (state) =>
	state.app.imageReferenceTemplatesApiStage;

export const videoAnyoneDefaultModelSelector = (state) => state.app.videoAnyoneDefaultModel;
export const videoAnyoneModelDataSelector = (state) => state.app.videoAnyoneModelData;
export const videoAnyoneModelsApiStageSelector = (state) => state.app.videoAnyoneModelsApiStage;

// music video
export const musicVideoCreateSceneModalSelector = (state) => state.app.musicVideoCreateSceneModal;

export const productPhotoGuideToggleSelector = (state) => state.app.productPhotoGuideToggle;
export default appSlice.reducer;
